import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/constants/testIds";

const links = [
  { href: "#collections", label: "Collections", tid: NAV.linkCollections },
  { href: "#atelier", label: "Atelier", tid: NAV.linkAtelier },
  { href: "#services", label: "Services", tid: NAV.linkServices },
  { href: "#buy-gold", label: "We Buy Gold", tid: NAV.linkBuyGold },
  { href: "#journal", label: "Instagram", tid: NAV.linkJournal },
  { href: "#contact", label: "Contact", tid: NAV.linkContact },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#F7F5F2]/80 backdrop-blur-xl border-b border-[#1C1C1A]/10"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-5">
        <a
          href="#top"
          data-testid={NAV.logo}
          className="font-serif text-xl md:text-2xl tracking-[0.18em] leading-none"
        >
          DIAMANT
          <span className="ml-2 font-serif tracking-[0.18em] leading-none">
            JEWELLERS
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.tid}
              className="link-underline text-xs uppercase tracking-[0.24em] font-sans"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          data-testid={NAV.menuToggle}
          className="md:hidden flex flex-col gap-1.5 py-2 px-1"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <span
            className={`w-6 h-px bg-[#1C1C1A] transition-transform ${
              open ? "translate-y-[6px] rotate-45" : ""
            }`}
          />
          <span
            className={`w-6 h-px bg-[#1C1C1A] transition-opacity ${
              open ? "opacity-0" : ""
            }`}
          />
          <span
            className={`w-6 h-px bg-[#1C1C1A] transition-transform ${
              open ? "-translate-y-[6px] -rotate-45" : ""
            }`}
          />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#F7F5F2] border-t border-[#1C1C1A]/10"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.tid}-mobile`}
                  onClick={() => setOpen(false)}
                  className="font-serif text-3xl"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid={`${NAV.ctaBook}-mobile`}
                className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-sans border border-[#1C1C1A] px-5 py-3 self-start"
              >
                Book a Visit →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
